import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Music } from 'lucide-react';
import logoImg from '../assets/LOGO.png';

const LANGS = [
  { code: 'de', fi: 'de', label: 'DE' },
  { code: 'en', fi: 'gb', label: 'EN' },
  { code: 'es', fi: 'es', label: 'ES' },
  { code: 'fr', fi: 'fr', label: 'FR' },
  { code: 'zh', fi: 'cn', label: 'ZH' },
  { code: 'ko', fi: 'kr', label: 'KO' },
  { code: 'th', fi: 'th', label: 'TH' },
  { code: 'ja', fi: 'jp', label: 'JA' },
];

export const NavBar = ({ onMusicToggle, musicPlaying }) => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const langRef = useRef(null);

  const currentLang = LANGS.find(l => l.code === (i18n.language?.toLowerCase() || 'en')) || LANGS[1];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close lang dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Lock body scroll when offcanvas open
  useEffect(() => {
    document.body.style.overflow = offcanvasOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [offcanvasOpen]);

  const switchLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lspa_lang', code);
    setLangOpen(false);
    setOffcanvasOpen(false);
  };

  const goToSection = (id) => {
    setOffcanvasOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    setOffcanvasOpen(false);
    if (location.pathname !== '/') navigate('/');
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinks = [
    { id: 'news', label: t('nav.news'), section: true },
    { id: 'resort', label: t('nav.resort'), section: true },
    { id: 'longevity', label: t('nav.longevity'), section: true },
    { id: 'book', label: t('nav.book'), section: true },
    { id: 'contact', label: t('nav.contact'), section: true },
    { id: 'gewinnspiel', label: t('nav.gewinnspiel'), section: false, path: '/gewinnspiel' },
  ];

  return (
    <>
      <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
        <div className="container">
          <div className="navbar-inner">
            {/* Logo */}
            <a href="/" className="navbar-logo" onClick={goHome}>
              <img src={logoImg} alt="Legend Spa Hotel Logo" />
              <div className="navbar-brand">
                <span className="navbar-brand-name">Legend Spa Hotel</span>
                <span className="navbar-brand-slogan">Legends Never Die</span>
              </div>
            </a>

            {/* Right controls */}
            <div className="navbar-right">
              {/* Music icon (mobile: icon only; desktop: pill) */}
              {onMusicToggle && (
                <button
                  className={`navbar-music-btn${musicPlaying ? ' playing' : ''}`}
                  onClick={onMusicToggle}
                  aria-label="Toggle Legend Sound"
                  data-testid="navbar-music-btn"
                >
                  <Music size={16} />
                  <span className="navbar-music-label">Legend Sound</span>
                  {musicPlaying && (
                    <span className="navbar-music-bars" aria-hidden="true">
                      <i /><i /><i />
                    </span>
                  )}
                </button>
              )}

              {/* Flag language dropdown */}
              <div className="lang-dropdown" ref={langRef}>
                <button
                  className="lang-dropdown-trigger"
                  onClick={() => setLangOpen(v => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={langOpen}
                  data-testid="lang-dropdown-trigger"
                >
                  <span className={`fi fi-${currentLang.fi}`} style={{ borderRadius: '2px' }} />
                  <span className="lang-code">{currentLang.label}</span>
                  <ChevronDown size={12} className={`lang-chevron${langOpen ? ' open' : ''}`} />
                </button>
                {langOpen && (
                  <div className="lang-dropdown-menu" role="listbox">
                    {LANGS.map(lang => (
                      <button
                        key={lang.code}
                        className={`lang-option${currentLang.code === lang.code ? ' active' : ''}`}
                        onClick={() => switchLang(lang.code)}
                        role="option"
                        aria-selected={currentLang.code === lang.code}
                        data-testid={`language-switcher-${lang.code}`}
                      >
                        <span className={`fi fi-${lang.fi}`} style={{ borderRadius: '2px' }} />
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Hamburger */}
              <button
                className="navbar-hamburger"
                onClick={() => setOffcanvasOpen(true)}
                data-testid="mobile-nav-open-button"
                aria-label="Open menu"
              >
                <span /><span /><span />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Offcanvas backdrop */}
      <div
        className={`offcanvas-backdrop${offcanvasOpen ? ' visible' : ''}`}
        onClick={() => setOffcanvasOpen(false)}
        aria-hidden="true"
      />

      {/* Offcanvas panel */}
      <aside className={`offcanvas-panel${offcanvasOpen ? ' open' : ''}`} aria-modal="true">
        <div className="offcanvas-header">
          <div className="offcanvas-brand">
            <img src={logoImg} alt="Legend Spa Hotel" className="offcanvas-logo" />
            <div>
              <p className="offcanvas-brand-name">Legend Spa Hotel</p>
              <p className="offcanvas-brand-slogan">Legends Never Die</p>
            </div>
          </div>
          <button
            className="offcanvas-close"
            onClick={() => setOffcanvasOpen(false)}
            data-testid="mobile-nav-close-button"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <hr className="offcanvas-divider" />

        <nav className="offcanvas-nav">
          {navLinks.map((link, i) => (
            <a
              key={link.id}
              href={link.section ? `/#${link.id}` : link.path}
              className="offcanvas-link"
              onClick={e => {
                e.preventDefault();
                if (link.section) {
                  goToSection(link.id);
                } else {
                  setOffcanvasOpen(false);
                  navigate(link.path);
                }
              }}
              style={{
                animationDelay: `${i * 0.06}s`,
                color: link.id === 'gewinnspiel' ? '#D4AF37' : undefined,
              }}
            >
              <span className="offcanvas-link-num">0{i + 1}</span>
              {link.label}
            </a>
          ))}
        </nav>

        <hr className="offcanvas-divider" />

        {/* Language in offcanvas */}
        <div className="offcanvas-langs">
          {LANGS.map(lang => (
            <button
              key={lang.code}
              className={`offcanvas-lang-btn${currentLang.code === lang.code ? ' active' : ''}`}
              onClick={() => switchLang(lang.code)}
              data-testid={`offcanvas-lang-${lang.code}`}
            >
              <span className={`fi fi-${lang.fi}`} style={{ borderRadius: '2px' }} />
              <span>{lang.label}</span>
            </button>
          ))}
        </div>

        <div className="offcanvas-footer">
          <p className="offcanvas-footer-text">Isla Margarita &middot; Venezuela &middot; Est. 2026</p>
        </div>
      </aside>
    </>
  );
};
